/**
 * useShellFireModes - the armed weapon's fire modes, and the resets that follow a change of weapon.
 *
 * - The MODES: Standard / Swift / Lightning in melee (Unwieldy and
 *   Unbalanced forbid Lightning only - Swift is legal RAW, v8.197.12), and
 *   S / Semi / Full at range from the rate of fire.
 * - THE MODE IS REMEMBERED PER WEAPON (v8.327.0): arming a weapon brings
 *   back the mode it was last fired in; one never fired opens on its first.
 *   The memory rides the per-weapon record beside the custom mods.
 * - Switching to a ranged weapon lowers the melee-only toggles; a new body
 *   type puts the aimed location back to Auto.
 *
 * Each effect fires on exactly what it always fired on - the weapon, the
 * mode, the kind of weapon, the body type - and reads everything else as it
 * stands in that render, through a ref refreshed every render (the values
 * its closure would have held). Extracted from DeathWatchSimulator.
 */
import { useEffect, useRef, useMemo } from 'react';
import { hasWS } from '../engine/weaponSpecials';
import { fireModeRefusal } from '../engine/fireModes';
import { bodyTypeOf } from './useShellActionDrum';

/** A mode with `refused` set when this shooter's state refuses it. */
function withRefusal(mode, actorConds, slot) {
  const refused = fireModeRefusal(actorConds, mode.key, slot);
  return refused ? { ...mode, refused } : mode;
}

/** The fire modes a slot offers, in button order. */
export function fireModesOf(slot, isMelee) {
  if (!slot || slot.isEmpty) return [];
  if (isMelee) return meleeModes(slot);
  const rof = slot.rateOfFire;
  return [
    rof.single && { key: 'single', label: 'S' },
    rof.semi > 0 && { key: 'semi', label: 'Semi/' + rof.semi },
    rof.full > 0 && { key: 'full', label: 'Full/' + rof.full },
  ].filter(Boolean);
}

/** Melee: Standard always; Swift and Lightning as the weapon allows. */
function meleeModes(slot) {
  const sp = slot.specialRules || [];
  const noLightning = hasWS(sp, 'Unwieldy') || hasWS(sp, 'Unbalanced');
  return [
    { key: 'single', label: 'Standard' },
    slot.canSwiftAttack && { key: 'swift', label: 'Swift' },
    slot.canLightningAttack && !noLightning && { key: 'lightning', label: 'Lightning' },
  ].filter(Boolean);
}

/** The mode to switch to on arming, or null to keep the current one. */
export function modeOnArming(fireModes, remembered, fireMode) {
  if (fireModes.length === 0) return null;
  const valid = (key) => fireModes.some((m) => m.key === key);
  if (remembered && valid(remembered)) return remembered !== fireMode ? remembered : null;
  return valid(fireMode) ? null : fireModes[0].key;
}

/** The per-weapon record with this weapon's mode remembered (the same record when unchanged). */
export function rememberMode(p, key, fireMode) {
  return p[key]?.fireMode === fireMode ? p : { ...p, [key]: { ...p[key], fireMode } };
}

/** Lowered melee-only toggles (the same record when none was up). */
export function withoutMeleeToggles(prev) {
  if (!prev.charge && !prev.allOutAttack && !prev.guardedAttack && !prev.gangUp) return prev;
  return { ...prev, charge: false, allOutAttack: false, guardedAttack: false, gangUp: false };
}

/** Arming a weapon restores its mode; firing in a mode remembers it. */
function useModeMemory({ selectedSlot, localCurrentProfile, fireMode, armedWeaponKey }, latest) {
  useEffect(() => {
    const { fireModes, perWeaponAttackState, setFireMode, armedWeaponKey: key } = latest.current;
    const remembered = key ? perWeaponAttackState[key]?.fireMode : undefined;
    const next = modeOnArming(fireModes, remembered, latest.current.fireMode);
    if (next) setFireMode(next);
  }, [selectedSlot, localCurrentProfile, latest]);
  useEffect(() => {
    const { fireModes, setPerWeaponAttackState } = latest.current;
    if (!armedWeaponKey || !fireModes.some((m) => m.key === fireMode)) return;
    setPerWeaponAttackState((p) => rememberMode(p, armedWeaponKey, fireMode));
  }, [fireMode, armedWeaponKey, latest]);
}

/** A ranged weapon lowers the melee-only toggles; a new body type resets the aimed location. */
function useWeaponKindResets({ isMelee, currentTab }, latest) {
  useEffect(() => {
    if (!isMelee) latest.current.setAttackToggles(withoutMeleeToggles);
  }, [isMelee, latest]);
  const currentBodyType = bodyTypeOf(currentTab?.target);
  useEffect(() => {
    latest.current.setTargetLocation('auto');
  }, [currentBodyType, latest]);
}

/**
 * @param {object} args - slot, isMelee, selectedSlot, localCurrentProfile,
 *   fireMode, setFireMode, armedWeaponKey, perWeaponAttackState,
 *   setPerWeaponAttackState, setAttackToggles, currentTab, setTargetLocation.
 * @returns {object[]} the armed weapon's fire modes.
 */
export default function useShellFireModes(args) {
  const { slot, isMelee, actorConds } = args;
  // Each mode with the reason it is refused while Pinned or after a run (2026-10-26), for the selector to dim.
  const fireModes = useMemo(
    () => fireModesOf(slot, isMelee).map((m) => withRefusal(m, actorConds, slot)),
    [slot, isMelee, actorConds],
  );
  const latest = useRef(null);
  latest.current = { ...args, fireModes };
  useModeMemory(args, latest);
  useWeaponKindResets(args, latest);
  return fireModes;
}
